<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { capitalize, computed, useTemplateRef } from 'vue'
import {
  RouteLocationRaw,
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router'

import UserProfileMenu from '../../../user/components/UserProfileMenu.vue'

import { useNavActive } from '../../../navigation/composables/useNavActive.ts'
import { useResolvedNav } from '../../../navigation/composables/useResolvedNav.ts'
import { useHasPermission } from '../../../permissions/composables/useHasPermission'
import { useSiteSlug } from '../../../sites/composables/useCurrentSite.ts'
import { useSiteContext } from '../../../sites/composables/useSiteContext.ts'
import { useSiteRouter } from '../../../sites/composables/useSiteRouter'
import { useNavigationDrawers } from '../../composables/useNavDrawer'

import { User } from '../../../users/models/user'

import { useFeatureFlagStore } from '../../../features/featureFlagStore.ts'
import { useNotificationStore } from '../../../notifications/notificationStore'
import { useAuthSiteStore } from '../../../user/authSiteStore'
import { useAuthUserStore } from '../../../user/authUserStore'

import imgUrl from '../../../assets/clinishift-logo-manager-portal-short.png'
import { useDashboardRoutes } from '../../../dashboard/useDashboardRoutes.ts'
import { orgHeaderNav } from '../../../navigation/orgHeaderNav.ts'

const { user } = defineProps<{
  user: User
}>()

const router = useRouter()
const { routes } = useSiteRouter()

const { site } = useSiteContext()
const authSiteStore = useAuthSiteStore()
const { sites } = storeToRefs(authSiteStore)

const authUserStore = useAuthUserStore()

const notificationStore = useNotificationStore()
const { unreadTotal: total } = storeToRefs(notificationStore)

async function logOutPressed() {
  try {
    authUserStore.signOut()
  } catch (error) {
    console.error('Logout failed', error)
  }
}

const { openDrawer, closeDrawer, isDrawerOpen } = useNavigationDrawers()

const isLeftDrawerOpen = isDrawerOpen('left')

onBeforeRouteUpdate(() => {
  closeDrawer('left')
})

const viewNotificationsPressed = () => router.push(routes.notifications())

const newShiftPressed = () => router.push(routes.shiftWizard())

// The wizard belongs to no nav link, so New Shift shows where you are
const route = useRoute()
const isNewShiftActive = computed(
  () => route.name === '/(app)/[org]/schedule/shifts.wizard',
)

const { can, canManageShifts } = useHasPermission()

const { dashboard } = useDashboardRoutes()
const siteMenu = useTemplateRef('site-menu')
const items = computed(() => {
  const menuItems = [] as { label: string; route: RouteLocationRaw }[]

  ;(sites.value ?? []).forEach((site) => {
    menuItems.push({
      label: site.name,
      route: dashboard(),
    })
  })

  return menuItems
})

const featureFlagStore = useFeatureFlagStore()
const org = useSiteSlug()
const nav = useResolvedNav(orgHeaderNav, () => ({
  org: org.value,
  can: (permission) => can(permission.ability, permission.subject),
  feature: (f) => featureFlagStore.isOn(f),
  manager: canManageShifts.value,
}))
const navItems = computed(() => nav.value.filter((n) => n.kind === 'link'))
const { isActive } = useNavActive()
</script>

<template>
  <nav class="bg-primary-900">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex h-16 items-center justify-between">
        <!-- Left Side Nav -->
        <div class="flex items-center gap-2">
          <ToggleButton
            class="bg-transparent text-white border-none"
            :model-value="isLeftDrawerOpen"
            @update:model-value="
              (value) => (value ? openDrawer('left') : closeDrawer('left'))
            "
          >
            <template #default>
              <i
                :class="{
                  'pi pi-times': isLeftDrawerOpen,
                  'pi pi-bars': !isLeftDrawerOpen,
                }"
              />
            </template>
          </ToggleButton>

          <div class="shrink-0">
            <Image width="32" height="32" :src="imgUrl" alt="CliniShift Logo" />
          </div>
          <div class="hidden md:block">
            <div class="ml-10 flex space-x-4">
              <Button
                v-for="item in navItems"
                :key="item.id"
                :to="item.to"
                class="ml-2 font-bold"
                :class="{
                  'bg-highlight-emphasis hover:bg-highlight': isActive(item),
                  'text-slate-50 hover:text-color': !isActive(item),
                }"
                as="router-link"
                :label="item.label"
                :severity="isActive(item) ? 'primary' : 'secondary'"
                text
              >
                <span>{{ capitalize(item.label) }}</span>
              </Button>
            </div>
          </div>
        </div>
        <!-- Right Side Nav -->
        <div class="flex items-center gap-x-4">
          <Button
            v-if="canManageShifts"
            label="New Shift"
            aria-label="New Shift"
            :aria-current="isNewShiftActive ? 'page' : undefined"
            :class="{
              'ring-2 ring-white ring-offset-2 ring-offset-primary-900':
                isNewShiftActive,
            }"
            @click="newShiftPressed"
          />

          <!-- Notifications are an org screen, which staff don't have this release (E2E-R4-01) -->
          <OverlayBadge
            v-if="canManageShifts"
            :value="total"
            size="small"
            :pt:pc-badge:root:class="{ 'hidden!': total === 0 }"
          >
            <Button
              class="text-white hover:text-color"
              icon="pi pi-bell"
              text
              aria-label="View notifications"
              @click="viewNotificationsPressed"
            />
          </OverlayBadge>

          <Button
            :label="site.name"
            icon="pi pi-chevron-down"
            icon-pos="right"
            severity="secondary"
            @click="siteMenu?.toggle"
          />
          <Menu ref="site-menu" :model="items" popup>
            <template #item="{ item, props }">
              <router-link v-slot="{ href, navigate }" :to="item.route" custom>
                <a :href="href" v-bind="props.action" @click="navigate">
                  <span class="ml-2">{{ item.label }}</span>
                </a>
              </router-link>
            </template>
          </Menu>

          <!-- Profile dropdown -->
          <UserProfileMenu :user="user" @log-out="logOutPressed" />
        </div>
      </div>
    </div>
  </nav>

  <Drawer
    :pt:content:class="'pr-0'"
    :visible="isLeftDrawerOpen"
    @update:visible="
      (value) => (value ? openDrawer('left') : closeDrawer('left'))
    "
  >
    <nav>
      <ul>
        <li v-for="item in navItems" :key="item.id">
          <Button
            :to="item.to"
            class="justify-start"
            :class="{
              'bg-highlight-emphasis hover:bg-highlight': isActive(item),
            }"
            as="router-link"
            :label="item.label"
            :severity="isActive(item) ? 'primary' : 'secondary'"
            size="large"
            text
            fluid
          />
        </li>
      </ul>
    </nav>
  </Drawer>
</template>
